// src/routes/domains/global/global.routes.tsx

import { lazy } from 'react';
import { createProtectedRoute } from '../../core/createProtectedRoute';
import type { AppRoute } from '../../core/types';

// ------------------------------
// Lazy‑loaded Global components (absolute imports)
// ------------------------------

// Global operations & compliance
const GlobalOperations = lazy(() => import('@/pages/global/GlobalOperations'));
const GlobalCompliance = lazy(() => import('@/pages/global/GlobalCompliance'));
const GlobalAnalytics = lazy(() => import('@/pages/global/GlobalAnalytics'));
const GlobalBilling = lazy(() => import('@/pages/global/GlobalBilling'));

// Global search & case management
const GlobalCaseSearch = lazy(() => import('@/pages/agency/GlobalCaseSearch'));
const GlobalSearchPage = lazy(() => import('@/pages/agency/GlobalSearch'));

// Global mobility & messaging
// 2026-08-25: old messenger archived (src/_archive/global-connect-v1/) — this route
// now renders the same Global Connect v2 unified shell as /global-connect and
// /global-connect-messages (see src/pages/community/GlobalConnect.tsx).
const GlobalMobilityMessenger = lazy(() => import('@/pages/community/GlobalConnect'));

// ------------------------------
// Global Routes Definition
// ------------------------------
export const globalRoutes: AppRoute[] = [
  // Global operations dashboards
  {
    id: 'global.operations',
    domain: 'global',
    path: '/global/operations',
    element: createProtectedRoute(<GlobalOperations />, {
      tier: 'premium',
      meta: {
        title: 'Global Operations Dashboard',
        region: 'Global',
        intent: 'dashboard',
        aiTags: ['global', 'operations', 'dashboard'],
      },
    }),
  },
  {
    id: 'global.compliance',
    domain: 'global',
    path: '/global/compliance',
    element: createProtectedRoute(<GlobalCompliance />, {
      tier: 'premium',
      meta: {
        title: 'Global Compliance Dashboard',
        region: 'Global',
        intent: 'dashboard',
        aiTags: ['global', 'compliance', 'regulations'],
      },
    }),
  },
  {
    id: 'global.analytics',
    domain: 'global',
    path: '/global/analytics',
    element: createProtectedRoute(<GlobalAnalytics />, {
      tier: 'premium',
      meta: {
        title: 'Global Analytics Dashboard',
        region: 'Global',
        intent: 'dashboard',
        aiTags: ['global', 'analytics', 'data'],
      },
    }),
  },
  {
    id: 'global.billing',
    domain: 'global',
    path: '/global/billing',
    element: createProtectedRoute(<GlobalBilling />, {
      tier: 'premium',
      meta: {
        title: 'Global Billing Dashboard',
        region: 'Global',
        intent: 'dashboard',
        aiTags: ['global', 'billing', 'finance'],
      },
    }),
  },

  // Global search & case management
  {
    id: 'global.case_search',
    domain: 'global',
    path: '/global/case-search',
    element: createProtectedRoute(<GlobalCaseSearch />, {
      tier: 'premium',
      meta: {
        title: 'Global Case Search',
        region: 'Global',
        intent: 'search',
        aiTags: ['global', 'case', 'search'],
      },
    }),
  },
  {
    id: 'global.search',
    domain: 'global',
    path: '/global/search',
    element: createProtectedRoute(<GlobalSearchPage />, {
      tier: 'premium',
      meta: {
        title: 'Global Search',
        region: 'Global',
        intent: 'search',
        aiTags: ['global', 'search'],
      },
    }),
  },

  // Global mobility & messaging
  {
    id: 'global.messenger',
    domain: 'global',
    path: '/global-messenger',
    element: createProtectedRoute(<GlobalMobilityMessenger />, {
      tier: 'premium',
      meta: {
        title: 'Global Mobility Messenger',
        region: 'Global',
        intent: 'chat',
        aiTags: ['global', 'messenger', 'chat'],
      },
    }),
  },

  // global.salary_calculator removed — GlobalSalaryCalculator archived (same job as
  // UKGlobalSalaryCalculator, which stays); path already redirected in uk.routes.tsx,
  // which loads before this domain and would have shadowed this entry regardless.
];